import { useEffect, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  LineChart,
  Line,
  Bar,
  Cell,
} from 'recharts';
import { api } from '@/lib/api';
import { usePeriodStore, monthIdsQuery } from '@/lib/periodStore';
import { useMonthStore } from '@/lib/monthStore';
import { GlassCard } from '@/components/ui/GlassCard';
import { PageHeader } from '@/components/ui/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { cn } from '@/lib/cn';
import { formatCurrency, formatNumber } from '@/lib/format';
import type {
  DailyForecast,
  MonthlyForecast,
  PredictionInsights,
  WeeklyForecast,
  AccuracyReport,
} from '@/types';

const TOOLTIP_STYLE = {
  background: '#0a0a0a',
  border: '2px solid #ff8a65',
  borderRadius: 0,
  boxShadow: '4px 4px 0 0 #000000',
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: '12px',
  color: '#f5f5f5',
};

export default function Prediccion() {
  const monthIds = usePeriodStore((s) => s.monthIds);
  const selectedMonthId = useMonthStore((s) => s.selectedMonthId);
  const [insights, setInsights] = useState<PredictionInsights | null>(null);
  const [daily, setDaily] = useState<DailyForecast | null>(null);
  const [weekly, setWeekly] = useState<WeeklyForecast | null>(null);
  const [monthly, setMonthly] = useState<MonthlyForecast | null>(null);
  const [accuracy, setAccuracy] = useState<AccuracyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [horizon, setHorizon] = useState(7);
  const [method, setMethod] = useState<'ensemble' | 'ema'>('ensemble');

  useEffect(() => {
    if (monthIds.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = monthIdsQuery(monthIds);
    Promise.all([
      api.get<PredictionInsights>(`/predict/insights?monthIds=${q}`),
      api.get<DailyForecast>(`/predict/daily?horizon=${horizon}&method=${method}&monthIds=${q}`),
      api.get<WeeklyForecast>(`/predict/weekly?method=${method}&monthIds=${q}`),
      api
        .get<AccuracyReport>(`/predict/accuracy?monthIds=${q}`)
        .catch(() => null),
      selectedMonthId
        ? api
            .get<MonthlyForecast>(`/predict/monthly/${selectedMonthId}?monthIds=${q}`)
            .catch(() => null)
        : Promise.resolve(null),
    ])
      .then(([i, d, w, a, m]) => {
        setInsights(i);
        setDaily(d);
        setWeekly(w);
        setAccuracy(a);
        setMonthly(m);
      })
      .catch((err) => {
        console.error('Error cargando predicción:', err);
      })
      .finally(() => setLoading(false));
  }, [monthIds, horizon, method, selectedMonthId]);

  if (monthIds.length === 0) {
    return (
      <>
        <PageHeader tag="// MÓDULO :: 05" title="Predicción" subtitle="Análisis BI y forecasting de ventas" />
        <EmptyState
          icon="auto_awesome"
          title="Selecciona un período"
          description="Usa el selector de arriba para elegir uno o más meses."
        />
      </>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <span className="material-symbols-outlined text-5xl text-primary animate-spin">
          progress_activity
        </span>
      </div>
    );
  }

  const weekdayColors = [
    '#ef4444',
    '#fb923c',
    '#ff8a65',
    '#22d3ee',
    '#a3e635',
    '#c084fc',
    '#fbbf24',
  ];

  return (
    <>
      <PageHeader
        tag="// MÓDULO :: 05 / FORECASTING"
        title="Predicción"
        subtitle="Análisis BI + forecasting con day-of-week, day-of-month y tendencias"
        action={
          <div className="flex flex-col gap-2 items-end">
            <div className="flex gap-1 border-2 border-outline bg-surface">
              {(['ensemble', 'ema'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setMethod(m)}
                  className={cn(
                    'px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest font-bold transition-colors duration-150',
                    method === m
                      ? 'bg-cyan text-black'
                      : 'text-text-muted hover:text-text-bright cursor-pointer',
                  )}
                  title={m === 'ensemble' ? 'Day-of-week + day-of-month + trend + seasonality' : 'Suavizado Exponencial'}
                >
                  {m === 'ensemble' ? 'ENSEMBLE' : 'EMA'}
                </button>
              ))}
            </div>
            <div className="flex gap-1 border-2 border-outline bg-surface">
              {([7, 14, 30] as const).map((h) => (
                <button
                  key={h}
                  onClick={() => setHorizon(h)}
                  className={cn(
                    'px-4 py-2 font-mono text-xs uppercase tracking-widest font-bold transition-colors duration-150',
                    horizon === h
                      ? 'bg-primary text-black'
                      : 'text-text-muted hover:text-text-bright cursor-pointer',
                  )}
                >
                  {h}d
                </button>
              ))}
            </div>
          </div>
        }
      />

      <div className="grid grid-cols-12 gap-5 mb-6">
        {/* BI Insights */}
        <GlassCard
          variant="kinetic"
          padding="md"
          rounded="none"
          className="col-span-12 lg:col-span-6"
        >
          <div className="mb-4 pb-3 border-b-2 border-outline flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-secondary border-2 border-black text-black">
              <span className="material-symbols-outlined">insights</span>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                // BI_INSIGHTS
              </p>
              <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                Resumen BI
              </h3>
            </div>
          </div>
          <div className="space-y-3">
            {insights && insights.top3Days.length > 0 && (
              <p className="text-sm font-mono">
                <span className="text-text-muted">// </span>
                <span className="text-text-bright">Domingo</span>
                <span className="text-text-muted"> es tu mejor día con </span>
                <span className="font-bold text-primary num">
                  {formatNumber(insights.weekdayPattern.find((d) => d.dayOfWeek === 0)?.avgUnits || 0)}
                </span>
                <span className="text-text-muted"> unidades en promedio.</span>
              </p>
            )}
            {insights && (
              <p className="text-sm font-mono">
                <span className="text-text-muted">// </span>
                <span className="text-text-bright">{insights.weekdayPattern.reduce((min, d) => d.avgUnits < min.avgUnits ? d : min, insights.weekdayPattern[0]).dayName}</span>
                <span className="text-text-muted"> es el más bajo (</span>
                <span className="font-bold text-warning num">
                  {formatNumber(insights.weekdayPattern.reduce((min, d) => d.avgUnits < min.avgUnits ? d : min, insights.weekdayPattern[0]).avgUnits)}
                </span>
                <span className="text-text-muted"> unidades).</span>
              </p>
            )}
            {insights?.top3Days[0] && (
              <p className="text-sm font-mono">
                <span className="text-text-muted">// </span>
                <span className="text-text-bright">Mejor día fue el </span>
                <span className="font-bold text-secondary num">{insights.top3Days[0].date}</span>
                <span className="text-text-bright"> con </span>
                <span className="font-bold text-primary num">
                  {formatNumber(insights.top3Days[0].units)}
                </span>
                <span className="text-text-muted"> unidades.</span>
              </p>
            )}
            {insights?.bestWeek && (
              <div className="mt-3 p-3 bg-surface border-2 border-tertiary">
                <p className="font-mono text-[10px] uppercase tracking-widest text-tertiary">
                  // FINES DE SEMANA FUERTES
                </p>
                <p className="font-mono text-sm text-text-bright mt-1">
                  Prepara más producto los fines de semana — staffing extra recomendado.
                </p>
              </div>
            )}
          </div>
        </GlassCard>

        {/* Recommendations */}
        <GlassCard
          variant="kinetic"
          padding="md"
          rounded="none"
          className="col-span-12 lg:col-span-6"
        >
          <div className="mb-4 pb-3 border-b-2 border-outline flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-tertiary border-2 border-black text-black">
              <span className="material-symbols-outlined">lightbulb</span>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-tertiary">
                // RECOMMENDATIONS
              </p>
              <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                Acciones
              </h3>
            </div>
          </div>
          <div className="space-y-3 text-sm font-mono">
            {insights && insights.weekdayPattern.length >= 2 && (() => {
              const sorted = [...insights.weekdayPattern].sort((a, b) => b.avgUnits - a.avgUnits);
              const best = sorted[0];
              const worst = sorted[sorted.length - 1];
              const ratio = worst.avgUnits > 0 ? (best.avgUnits / worst.avgUnits).toFixed(1) : '∞';
              return (
                <p>
                  <span className="text-text-muted">// Prepara </span>
                  <span className="font-bold text-primary num">{ratio}x</span>
                  <span className="text-text-muted"> más producto para el </span>
                  <span className="font-bold text-text-bright">{best.dayName}</span>
                  <span className="text-text-muted"> que para el </span>
                  <span className="font-bold text-text-bright">{worst.dayName}</span>
                  <span className="text-text-muted">.</span>
                </p>
              );
            })()}
            {insights?.bestWeek && (
              <p>
                <span className="text-text-muted">// Tu mejor semana promedió </span>
                <span className="font-bold text-primary num">
                  {formatNumber(
                    Math.round(
                      insights.bestWeek.units / 7,
                    ),
                  )}
                </span>
                <span className="text-text-muted"> unidades/día. Replica las condiciones de esa semana.</span>
              </p>
            )}
          </div>
        </GlassCard>
      </div>

      {/* Daily Forecast */}
      {daily && daily.predictions.length > 0 && (
        <GlassCard variant="kinetic" padding="md" rounded="none" className="mb-6">
          <div className="mb-4 pb-3 border-b-2 border-outline flex justify-between items-end">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                // DAILY_FORECAST :: {daily.method}
              </p>
              <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                Predicción Diaria — próximos {daily.horizon}d
              </h3>
              <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
                // Historia: {daily.historyDaysUsed} días
              </p>
            </div>
            <div className="flex gap-3 font-mono text-[10px] uppercase tracking-widest">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-primary" /> Predicho
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-primary opacity-30" /> ±1σ
              </span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={daily.predictions}>
              <defs>
                <linearGradient id="bandGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ff8a65" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#ff8a65" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis
                dataKey="date"
                stroke="#9ca3af"
                fontSize={10}
                tickLine={false}
                axisLine={{ stroke: '#525252' }}
                tickFormatter={(d) => d.slice(5)}
              />
              <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={{ stroke: '#525252' }} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Area
                type="monotone"
                dataKey="upper"
                stroke="transparent"
                fill="url(#bandGrad)"
              />
              <Area
                type="monotone"
                dataKey="lower"
                stroke="transparent"
                fill="#0a0a0a"
              />
              <Area
                type="monotone"
                dataKey="predicted"
                stroke="#ff8a65"
                strokeWidth={3}
                fill="transparent"
                dot={{ r: 5, fill: '#ff8a65', stroke: '#000', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 border-t-2 border-outline pt-4">
            <Stat label="Promedio" value={daily.predictions.reduce((s, p) => s + p.predicted, 0) / daily.predictions.length} />
            <Stat label="Mejor día" value={Math.max(...daily.predictions.map((p) => p.predicted))} />
            <Stat label="Peor día" value={Math.min(...daily.predictions.map((p) => p.predicted))} />
            <Stat
              label="Confianza avg"
              value={daily.predictions.reduce((s, p) => s + p.confidence, 0) / daily.predictions.length * 100}
              suffix="%"
            />
          </div>
        </GlassCard>
      )}

      <div className="grid grid-cols-12 gap-5 mb-6">
        {/* Weekday Pattern */}
        {insights && insights.weekdayPattern.length > 0 && (
          <GlassCard
            variant="kinetic"
            padding="md"
            rounded="none"
            className="col-span-12 lg:col-span-7"
          >
            <div className="mb-4 pb-3 border-b-2 border-outline">
              <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                // WEEKDAY_PATTERN
              </p>
              <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                Patrón por Día de Semana
              </h3>
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={insights.weekdayPattern}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis
                  dataKey="dayName"
                  stroke="#9ca3af"
                  fontSize={11}
                  tickLine={false}
                  axisLine={{ stroke: '#525252' }}
                />
                <YAxis stroke="#9ca3af" fontSize={11} tickLine={false} axisLine={{ stroke: '#525252' }} />
                <Tooltip contentStyle={TOOLTIP_STYLE} />
                <Bar dataKey="avgUnits" stroke="#000" strokeWidth={2}>
                  {insights.weekdayPattern.map((_d, i) => (
                    <Cell key={i} fill={weekdayColors[i % weekdayColors.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </GlassCard>
        )}

        {/* Top Days */}
        {insights && insights.top3Days.length > 0 && (
          <GlassCard
            variant="kinetic"
            padding="md"
            rounded="none"
            className="col-span-12 lg:col-span-5"
          >
            <div className="mb-4 pb-3 border-b-2 border-outline">
              <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                // TOP_DAYS
              </p>
              <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                Top Días Históricos
              </h3>
            </div>
            <div className="space-y-3">
              {insights.top3Days.map((d, i) => (
                <div
                  key={d.date}
                  className="flex items-center gap-3 bg-surface border-2 border-outline p-3"
                >
                  <div
                    className={cn(
                      'w-10 h-10 flex items-center justify-center border-2 border-black font-condensed text-2xl',
                      i === 0 ? 'bg-primary text-black' : 'bg-surface-container text-text-bright',
                    )}
                  >
                    #{i + 1}
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-text-bright num">
                      {formatNumber(d.units)} unidades
                    </p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
                      {d.date} · {formatCurrency(d.revenue)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>
        )}
      </div>

      {/* Weekly Forecast + Monthly Forecast */}
      <div className="grid grid-cols-12 gap-5">
        {weekly && weekly.weeks.length > 0 && (
          <GlassCard
            variant="kinetic"
            padding="md"
            rounded="none"
            className="col-span-12 lg:col-span-6"
          >
            <div className="mb-4 pb-3 border-b-2 border-outline">
              <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                // WEEKLY_FORECAST
              </p>
              <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                Predicción Semanal
              </h3>
            </div>
            <div className="space-y-3">
              {weekly.weeks.map((w, i) => (
                <div
                  key={w.weekStart}
                  className="bg-surface border-2 border-outline p-3"
                >
                  <div className="flex justify-between items-center">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted">
                      Sem {i + 1} · {w.weekStart} → {w.weekEnd}
                    </p>
                    <p className="font-condensed text-2xl text-text-bright num">
                      {formatNumber(w.predicted)} unid
                    </p>
                  </div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1 num">
                    Rango: {formatNumber(w.lower)} – {formatNumber(w.upper)}
                  </p>
                </div>
              ))}
            </div>
          </GlassCard>
        )}

        {monthly && (
          <GlassCard
            variant="glow"
            padding="md"
            rounded="none"
            className="col-span-12 lg:col-span-6"
          >
            <div className="mb-4 pb-3 border-b-2 border-primary">
              <p className="font-mono text-[10px] uppercase tracking-widest text-primary">
                // MONTHLY_FORECAST
              </p>
              <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                Predicción Mensual
              </h3>
            </div>
            <div className="space-y-3">
              <div className="bg-surface border-2 border-primary p-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-primary mb-1">
                  PRÓXIMO MES
                </p>
                <p className="font-condensed text-4xl text-text-bright num leading-none">
                  {formatNumber(monthly.predictedUnits)} unid
                </p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1 num">
                  {formatCurrency(monthly.predictedRevenue)} esperados
                </p>
              </div>
              <div className="bg-surface border-2 border-outline p-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mb-1">
                  CONFIANZA
                </p>
                <p className="font-condensed text-3xl text-tertiary num leading-none">
                  {(monthly.confidence * 100).toFixed(0)}%
                </p>
              </div>
              <div className="bg-surface border-2 border-outline p-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mb-1">
                  RANGO IC 90%
                </p>
                <p className="font-mono text-sm text-text-bright num">
                  {formatNumber(monthly.lowerUnits)} – {formatNumber(monthly.upperUnits)} unidades
                </p>
              </div>
              {/* Escenarios What-If (PDF 5.3) */}
              {monthly.scenarios && (
                <div className="border-t-2 border-outline pt-3">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-secondary mb-2">
                    // WHAT-IF_SCENARIOS
                  </p>
                  <div className="space-y-2">
                    <div className="bg-surface border-2 border-error p-2 flex justify-between items-center">
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-error font-bold">
                          PESIMISTA ×0.75
                        </p>
                      </div>
                      <p className="font-condensed text-xl text-text-bright num">
                        {formatNumber(monthly.scenarios.pesimista.units)}
                      </p>
                    </div>
                    <div className="bg-surface border-2 border-tertiary p-2 flex justify-between items-center">
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-tertiary font-bold">
                          PROBABLE ×1.0
                        </p>
                      </div>
                      <p className="font-condensed text-xl text-text-bright num">
                        {formatNumber(monthly.scenarios.probable.units)}
                      </p>
                    </div>
                    <div className="bg-surface border-2 border-primary p-2 flex justify-between items-center">
                      <div>
                        <p className="font-mono text-[10px] uppercase tracking-widest text-primary font-bold">
                          OPTIMISTA ×1.25
                        </p>
                      </div>
                      <p className="font-condensed text-xl text-text-bright num">
                        {formatNumber(monthly.scenarios.optimista.units)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </GlassCard>
        )}
      </div>

      {/* === MODEL ACCURACY (CV + MAPE) === */}
      {accuracy && (
        <section className="mt-6">
          <GlassCard variant="kinetic" padding="md" rounded="none">
            <div className="mb-4 pb-3 border-b-2 border-outline">
              <p className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                // MODEL_ACCURACY :: {accuracy.method}
              </p>
              <h3 className="font-condensed text-2xl uppercase tracking-tight text-text-bright mt-1">
                Precisión del Modelo (Backtest)
              </h3>
              <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
                // {accuracy.sampleSize} días evaluados · predicción vs realidad
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              {/* MAPE */}
              <div
                className={cn(
                  'bg-surface border-2 p-4 shadow-[3px_3px_0_0_#000000]',
                  accuracy.mape < 10
                    ? 'border-tertiary'
                    : accuracy.mape < 20
                      ? 'border-secondary'
                      : 'border-error',
                )}
              >
                <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mb-1">
                  MAPE
                </p>
                <p
                  className={cn(
                    'font-condensed text-3xl num leading-none',
                    accuracy.mape < 10
                      ? 'text-tertiary'
                      : accuracy.mape < 20
                        ? 'text-cyan'
                        : 'text-error',
                  )}
                >
                  {accuracy.mape.toFixed(2)}%
                </p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
                  {accuracy.mape < 10 ? 'EXCELENTE' : accuracy.mape < 20 ? 'BUENO' : 'AJUSTAR'}
                </p>
              </div>
              {/* CV */}
              <div
                className={cn(
                  'bg-surface border-2 p-4 shadow-[3px_3px_0_0_#000000]',
                  accuracy.cv < 0.3
                    ? 'border-tertiary'
                    : accuracy.cv < 0.5
                      ? 'border-secondary'
                      : 'border-error',
                )}
              >
                <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mb-1">
                  CV
                </p>
                <p
                  className={cn(
                    'font-condensed text-3xl num leading-none',
                    accuracy.cv < 0.3
                      ? 'text-tertiary'
                      : accuracy.cv < 0.5
                        ? 'text-cyan'
                        : 'text-error',
                  )}
                >
                  {accuracy.cv.toFixed(3)}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
                  {accuracy.cvClassification}
                </p>
              </div>
              {/* RMSE */}
              <div className="bg-surface border-2 border-outline p-4 shadow-[3px_3px_0_0_#000000]">
                <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mb-1">
                  RMSE
                </p>
                <p className="font-condensed text-3xl text-text-bright num leading-none">
                  {accuracy.rmse.toFixed(1)}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
                  Error cuadrático
                </p>
              </div>
              {/* MAE */}
              <div className="bg-surface border-2 border-outline p-4 shadow-[3px_3px_0_0_#000000]">
                <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mb-1">
                  MAE
                </p>
                <p className="font-condensed text-3xl text-text-bright num leading-none">
                  {accuracy.meanAbsoluteError.toFixed(1)}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mt-1">
                  Error absoluto medio
                </p>
              </div>
            </div>
            {accuracy.mapeByWeek.length > 0 && (
              <div>
                <p className="font-mono text-[10px] uppercase tracking-widest text-secondary mb-2">
                  // MAPE_POR_SEMANA (últimas {accuracy.mapeByWeek.length})
                </p>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={accuracy.mapeByWeek}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                    <XAxis
                      dataKey="weekStart"
                      stroke="#9ca3af"
                      fontSize={10}
                      tickLine={false}
                      axisLine={{ stroke: '#525252' }}
                    />
                    <YAxis
                      stroke="#9ca3af"
                      fontSize={11}
                      tickLine={false}
                      axisLine={{ stroke: '#525252' }}
                      tickFormatter={(v) => `${v}%`}
                    />
                    <Tooltip
                      contentStyle={{
                        background: '#0a0a0a',
                        border: '2px solid #ff8a65',
                        borderRadius: 0,
                        fontFamily: 'JetBrains Mono, monospace',
                        fontSize: '12px',
                        color: '#f5f5f5',
                      }}
                      formatter={(v: number) => [`${v.toFixed(2)}%`, 'MAPE']}
                    />
                    <Line
                      type="monotone"
                      dataKey="mape"
                      stroke="#ff8a65"
                      strokeWidth={3}
                      dot={{ r: 4, fill: '#ff8a65', stroke: '#000', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </GlassCard>
        </section>
      )}
    </>
  );
}

function Stat({ label, value, suffix }: { label: string; value: number; suffix?: string }) {
  return (
    <div className="bg-surface border-2 border-outline p-3">
      <p className="font-mono text-[10px] uppercase tracking-widest text-text-muted mb-1">
        {label}
      </p>
      <p className="font-condensed text-2xl text-text-bright num leading-none">
        {formatNumber(value)}
        {suffix && (
          <span className="font-mono text-xs text-text-muted ml-1">{suffix}</span>
        )}
      </p>
    </div>
  );
}
